// Minimal SSE consumer: POST + fetch + ReadableStream, parses Vercel-style
// `data: {json}\n\n` chunks and dispatches typed events. Mirrors the
// authorization pattern used by AssistantProvider's DefaultChatTransport.

import type { SlideshowEvent } from './types';

interface StreamOptions {
    url: string;
    body: unknown;
    token: string;
    signal?: AbortSignal;
    onEvent: (event: SlideshowEvent) => void;
}

const DONE_MARKER = '[DONE]';

export const streamSlideshowSSE = async ({
    url,
    body,
    token,
    signal,
    onEvent,
}: StreamOptions): Promise<void> => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        throw new Error(
            `SSE request failed (${response.status}): ${text || response.statusText}`,
        );
    }

    const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

    let buffer = '';
    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += value;

            let separatorIndex = buffer.indexOf('\n\n');
            while (separatorIndex !== -1) {
                const rawEvent = buffer.slice(0, separatorIndex);
                buffer = buffer.slice(separatorIndex + 2);
                separatorIndex = buffer.indexOf('\n\n');

                const dataLines = rawEvent
                    .split('\n')
                    .filter((line) => line.startsWith('data:'))
                    .map((line) => line.slice(5).trimStart());
                if (dataLines.length === 0) continue;

                const data = dataLines.join('\n');
                if (data === DONE_MARKER) return;
                try {
                    onEvent(JSON.parse(data) as SlideshowEvent);
                } catch (err) {
                    console.error('Failed to parse SSE event:', data, err);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
};
